type JsonObject = Record<string, unknown>;

type ResponseKind = "form" | "proxy" | "agency" | "type";

const schemaMapKeywords = [
	"properties",
	"patternProperties",
	"definitions",
	"$defs",
	"dependentSchemas"
];

const schemaListKeywords = [
	"allOf",
	"anyOf",
	"oneOf",
	"prefixItems"
];

const schemaKeywords = [
	"additionalProperties",
	"additionalItems",
	"contains",
	"propertyNames",
	"not",
	"if",
	"then",
	"else",
	"unevaluatedProperties",
	"unevaluatedItems",
	"contentSchema"
];

const fieldMetadataKeys = [
	"configuration",
	"fieldCode",
	"modelProperty",
	"maxSize",
	"valueTime",
	"timeZoneMode"
];

function isObject(value: unknown): value is JsonObject {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

const tv4Keywords: Record<string, string> = {
	0: "type",
	1: "enum",
	10: "anyOf",
	11: "oneOf",
	12: "oneOf",
	13: "not",
	100: "multipleOf",
	101: "minimum",
	102: "exclusiveMinimum",
	103: "maximum",
	104: "exclusiveMaximum",
	200: "minLength",
	201: "maxLength",
	202: "pattern",
	300: "minProperties",
	301: "maxProperties",
	302: "required",
	303: "additionalProperties",
	304: "dependencies",
	400: "minItems",
	401: "maxItems",
	402: "uniqueItems",
	403: "additionalItems",
	500: "format"
};

const messageVariants = ["info", "success", "warning", "error"];

function validationMessage(value: unknown): string | JsonObject | undefined {
	if(typeof value === "string") {
		return value;
	}

	if(!isObject(value)) {
		return undefined;
	}

	const result: JsonObject = {};

	for(const [code, message] of Object.entries(value)) {
		const keyword = code === "default" || code === "_" ? "_" : tv4Keywords[code];

		if(keyword && typeof message === "string" && result[keyword] === undefined) {
			result[keyword] = message;
		}
	}

	return Object.keys(result).length ? result : undefined;
}

function resolveLocalSchema(value: unknown, root: JsonObject, visited = new Set<unknown>()): JsonObject | undefined {
	if(!isObject(value) || visited.has(value)) {
		return undefined;
	}

	if(typeof value.$ref !== "string") {
		return value;
	}

	visited.add(value);

	const rootId = typeof root.$id === "string" ? root.$id.replace(/#.*$/, "") : "";
	const reference = rootId && value.$ref.startsWith(`${rootId}#`) ? value.$ref.slice(rootId.length) : value.$ref;

	if(reference !== "#" && !reference.startsWith("#/")) {
		return value;
	}

	let target: unknown = root;

	for(const key of reference.slice(2).split("/")) {
		if(reference === "#") {
			break;
		}

		target = isObject(target) ? target[key.replace(/~1/g, "/").replace(/~0/g, "~")] : undefined;
	}

	return resolveLocalSchema(target, root, visited) ?? value;
}

function legacyPresentationMetadata(schema: JsonObject): JsonObject {
	const metadata = isObject(schema.metadata) ? schema.metadata : {};

	return isObject(metadata.ui) ? metadata.ui : {};
}

function messagesFor(schema: JsonObject): JsonObject[] {
	const messages = legacyPresentationMetadata(schema).messages;
	const properties = isObject(schema.properties) ? schema.properties : {};
	const types = Array.isArray(schema.type) ? schema.type : [schema.type];

	if(!types.includes("object") && (schema.type !== undefined || !schema.properties)) {
		return [];
	}

	return Array.isArray(messages) ? messages.filter((message): message is JsonObject => {
		if(!isObject(message) || typeof message.markdown !== "string" || !message.markdown.trim()) {
			return false;
		}

		if(message.variant !== undefined && !messageVariants.includes(String(message.variant))) {
			return false;
		}

		if(message.before !== undefined && message.after !== undefined) {
			return false;
		}

		return ["before", "after"].every(anchor => message[anchor] === undefined
			|| typeof message[anchor] === "string" && Object.prototype.hasOwnProperty.call(properties, String(message[anchor])));
	}) : [];
}

function introFor(schema: JsonObject): string | undefined {
	const description = legacyPresentationMetadata(schema).description;

	if(typeof schema.description !== "string" || !schema.description.trim()) {
		return undefined;
	}

	const format = isObject(description) ? description.format : undefined;

	return format === undefined || format === "markdown"
		? schema.description
		: schema.description.replace(/[!-/:-@[-`{-~]/g, "\\$&");
}

function messageLabel(text: string, variant?: unknown): JsonObject {
	return {
		type: "Label",
		text,
		...(typeof variant === "string" && messageVariants.includes(variant) ? {
			options: {
				variant
			}
		} : {})
	};
}

/** Clone only UI schema members; arbitrary option/data literals are not schema trees. */
function convertLegacyUi(value: unknown): unknown {
	if(!isObject(value)) {
		return value;
	}

	const element = {
		...value
	};

	if(element.type === "Markdown" && typeof element.markdown === "string") {
		const options = isObject(element.options) ? {
			...element.options
		} : {};
		const variant = options.variant ?? element.variant;

		element.type = "Label";
		element.text = element.markdown;
		delete element.markdown;
		delete element.variant;

		if(typeof variant === "string" && messageVariants.includes(variant)) {
			options.variant = variant;
		}

		if(Object.keys(options).length) {
			element.options = options;
		}
	}

	if(Array.isArray(element.elements)) {
		element.elements = element.elements.map(convertLegacyUi);
	}

	if(isObject(element.options) && isObject(element.options.detail)) {
		element.options = {
			...element.options,
			detail: convertLegacyUi(element.options.detail)
		};
	}

	return element;
}

function hasLegacyPresentation(value: unknown, root: JsonObject, includeIntro = false, visited = new Set<JsonObject>()): boolean {
	const schema = resolveLocalSchema(value, root);

	if(!schema || visited.has(schema)) {
		return false;
	}

	const ancestors = new Set(visited).add(schema);
	const oldUi = isObject(schema["x-ui"]) ? schema["x-ui"] : {};

	return messagesFor(schema).length > 0 || Boolean(includeIntro && introFor(schema)) || oldUi.jsonForms !== undefined
		|| Object.values(isObject(schema.properties) ? schema.properties : {}).some(child => hasLegacyPresentation(child, root, false, ancestors))
		|| (Array.isArray(schema.items)
			? schema.items.some(item => hasLegacyPresentation(item, root, true, ancestors))
			: hasLegacyPresentation(schema.items, root, true, ancestors));
}

function controlScopes(ui: JsonObject): string[] {
	if(ui.type === "Control" && typeof ui.scope === "string") {
		return [ui.scope];
	}

	return Array.isArray(ui.elements) ? ui.elements.flatMap(child => isObject(child) ? controlScopes(child) : []) : [];
}

function controlPlacement(ui: JsonObject, scope: string, matchObjectLayout = true): {
	layout: JsonObject;
	index: number;
} | undefined {
	const elements = Array.isArray(ui.elements) ? ui.elements : [];

	for(let index = 0; index < elements.length; index++) {
		const child = elements[index];

		if(!isObject(child)) {
			continue;
		}

		const scopes = controlScopes(child);

		if(child.type === "Control" && child.scope === scope
			|| matchObjectLayout && scopes.length > 0 && scopes.every(candidate => candidate.startsWith(`${scope}/properties/`))) {
			return {
				layout: ui,
				index
			};
		}

		const nested = controlPlacement(child, scope, matchObjectLayout);

		if(nested) {
			return nested;
		}
	}

	return undefined;
}

function controlsAt(ui: JsonObject, scope: string): JsonObject[] {
	if(ui.type === "Control" && ui.scope === scope) {
		return [ui];
	}

	return Array.isArray(ui.elements) ? ui.elements.flatMap(child => isObject(child) ? controlsAt(child, scope) : []) : [];
}

function decoratePresentation(schema: JsonObject, root: JsonObject, ui: JsonObject, scope = "#", includeIntro = false, visited = new Set<JsonObject>()): JsonObject {
	const resolved = resolveLocalSchema(schema, root) ?? schema;

	if(visited.has(resolved)) {
		return ui;
	}

	const ancestors = new Set(visited).add(resolved);
	const properties = isObject(resolved.properties) ? resolved.properties : {};
	const messages = messagesFor(resolved);
	const intro = includeIntro ? introFor(resolved) : undefined;
	const layout: JsonObject = messages.length || intro
		? Array.isArray(ui.elements) ? ui : {
			type: "VerticalLayout",
			elements: [ui]
		}
		: ui;

	if(Array.isArray(layout.elements)) {
		const leading = messages.filter(message => message.before === undefined && message.after === undefined)
			.map(message => messageLabel(String(message.markdown), message.variant));

		if(intro && !(ui.type === "Control" && ui.scope === scope)) {
			leading.unshift(messageLabel(intro));
		}

		const existing = layout.elements.filter(isObject);
		const unique = leading.filter(label => !existing.some(element => element.type === "Label" && element.text === label.text
			&& (isObject(element.options) ? element.options.variant : undefined) === (isObject(label.options) ? label.options.variant : undefined)));
		const heading = existing[0]?.type === "Label" && existing[0]?.text === resolved.title ? 1 : 0;

		layout.elements.splice(heading, 0, ...unique);

		const unplaced = new Set<string>();

		for(const key of Object.keys(properties)) {
			const propertyScope = `${scope}/properties/${key.replace(/~/g, "~0").replace(/\//g, "~1")}`;
			const placement = controlPlacement(layout, propertyScope);
			const elements = placement?.layout.elements;

			if(placement && Array.isArray(elements)) {
				const before = messages.filter(message => message.before === key).map(message => messageLabel(String(message.markdown), message.variant));
				const after = messages.filter(message => message.after === key).map(message => messageLabel(String(message.markdown), message.variant));

				elements.splice(placement.index, 1, ...before, elements[placement.index], ...after);
			} else {
				unplaced.add(key);
			}
		}

		const compositor = controlPlacement(layout, scope, false);
		const elements = compositor?.layout.elements;

		if(compositor && Array.isArray(elements)) {
			const before = messages.filter(message => typeof message.before === "string" && unplaced.has(message.before))
				.map(message => messageLabel(String(message.markdown), message.variant));
			const after = messages.filter(message => typeof message.after === "string" && unplaced.has(message.after))
				.map(message => messageLabel(String(message.markdown), message.variant));

			elements.splice(compositor.index, 1, ...before, elements[compositor.index], ...after);
		}
	}

	for(const [key, child] of Object.entries(properties)) {
		const propertyScope = `${scope}/properties/${key.replace(/~/g, "~0").replace(/\//g, "~1")}`;
		const property = resolveLocalSchema(child, root);

		if(!property || !hasLegacyPresentation(property, root, false, ancestors)) {
			continue;
		}

		const controls = controlsAt(layout, propertyScope);

		for(const control of controls) {
			const options = isObject(control.options) ? {
				...control.options
			} : {};
			const item = Array.isArray(property.items) ? undefined : resolveLocalSchema(property.items, root);
			const detailSchema = item ?? property;
			const detail = presentationUi(detailSchema, root, options.detail, Boolean(item), ancestors);

			options.detail = detail;
			control.options = options;
		}

		if(!controls.length) {
			const placement = controlPlacement(layout, propertyScope);
			const childUi = Array.isArray(placement?.layout.elements) ? placement.layout.elements[placement.index] : undefined;

			if(isObject(childUi) && Array.isArray(childUi.elements)) {
				Object.assign(childUi, decoratePresentation(property, root, childUi, propertyScope, true, ancestors));
			}
		}
	}

	if(resolved.items !== undefined && !Array.isArray(resolved.items)) {
		const item = resolveLocalSchema(resolved.items, root);

		if(item && hasLegacyPresentation(item, root, true, ancestors)) {
			for(const control of controlsAt(layout, scope)) {
				const options = isObject(control.options) ? {
					...control.options
				} : {};

				options.detail = presentationUi(item, root, options.detail, true, ancestors);
				control.options = options;
			}
		}
	}

	return layout;
}

function presentationUi(schema: JsonObject, root: JsonObject, authored?: unknown, includeIntro = false, visited = new Set<JsonObject>()): JsonObject {
	const resolved = resolveLocalSchema(schema, root) ?? schema;
	const metadata = isObject(resolved.metadata) ? resolved.metadata : {};

	if(isObject(metadata.form)) {
		return metadata.form;
	}

	const oldUi = isObject(resolved["x-ui"]) ? resolved["x-ui"] : {};
	const source = authored ?? oldUi.jsonForms;
	const properties = isObject(resolved.properties) ? resolved.properties : undefined;
	const types = Array.isArray(resolved.type) ? resolved.type : [resolved.type];
	const object = properties !== undefined || types.includes("object");
	const alternatives = resolved.oneOf || resolved.anyOf || resolved.allOf || types.filter(type => type !== "null").length > 1;
	const generated = object && !alternatives
		? {
			type: "VerticalLayout",
			elements: Object.keys(properties ?? {}).map(key => ({
				type: "Control",
				scope: `#/properties/${key.replace(/~/g, "~0").replace(/\//g, "~1")}`
			}))
		}
		: {
			type: "Control",
			scope: "#"
		};
	const ui = isObject(source) ? convertLegacyUi(source) as JsonObject : generated;

	return decoratePresentation(resolved, root, ui, "#", includeIntro, visited);
}

function readMember(value: unknown, name: string): unknown {
	if(!isObject(value)) {
		return undefined;
	}

	const pascalName = name.charAt(0)
		.toUpperCase() + name.slice(1);

	return value[name] ?? value[pascalName];
}

function camelCaseConfiguration(value: unknown): unknown {
	if(Array.isArray(value)) {
		return value.map(camelCaseConfiguration);
	}

	if(!isObject(value)) {
		return value;
	}

	return Object.fromEntries(Object.entries(value)
		.map(([key, child]) => [
			key.charAt(0)
				.toLowerCase() + key.slice(1),
			camelCaseConfiguration(child)
		]));
}

function convertSchemaNode(value: unknown, root: JsonObject): unknown {
	if(!isObject(value)) {
		return value;
	}

	const schema = {
		...value
	};
	const metadata: JsonObject = isObject(schema.metadata) ? {
		...schema.metadata
	} : {};
	const legacyDateBoundary = schema.valueTime !== undefined;

	for(const key of fieldMetadataKeys) {
		if(schema[key] !== undefined && metadata[key] === undefined) {
			const legacyValue = schema[key];

			if(key === "configuration") {
				metadata[key] = camelCaseConfiguration(legacyValue);
			}
			else if(key === "maxSize" && typeof legacyValue === "string" && /^\d+$/.test(legacyValue)) {
				metadata[key] = Number(legacyValue);
			}
			else if(key === "modelProperty" && typeof legacyValue === "string") {
				metadata[key] = legacyValue.charAt(0)
					.toLowerCase() + legacyValue.slice(1);
			}
			else if(key === "valueTime" && typeof legacyValue === "string") {
				metadata[key] = legacyValue.toLowerCase();
			}
			else {
				metadata[key] = legacyValue;
			}
		}

		delete schema[key];
	}

	const legacyType = schema.type;
	const stringType = legacyType === "string" || (Array.isArray(legacyType) && legacyType.includes("string"));

	if(legacyType === "date" || legacyType === "datetime") {
		schema.type = "string";
		schema.format = "date-time";

		if(legacyType === "date" && metadata.dateOnly === undefined) {
			metadata.dateOnly = true;
		}
	}
	else if(stringType && (schema.format === "datetime" || (schema.format === "date" && legacyDateBoundary))) {
		if(schema.format === "date" && metadata.dateOnly === undefined) {
			metadata.dateOnly = true;
		}

		schema.format = "date-time";
	}
	else if(legacyType === "entity" || legacyType === "datapoint") {
		schema.type = "number";
		schema.format = legacyType;
	}
	else if(legacyType === "file" || (legacyType === "string" && schema.format === "base64")) {
		schema.type = "string";
		schema.format = "file";
	}
	else if(legacyType === "boolean" && schema.format === "checkbox") {
		delete schema.format;
	}

	if(schema.errorMessage === undefined) {
		const message = validationMessage(schema.validationMessage ?? schema["x-validationMessage"]);

		if(message !== undefined) {
			schema.errorMessage = message;
		}
	}

	delete schema.validationMessage;
	delete schema["x-validationMessage"];

	if(Object.keys(metadata).length > 0) {
		schema.metadata = metadata;
	}

	for(const key of schemaMapKeywords) {
		if(isObject(schema[key])) {
			schema[key] = Object.fromEntries(Object.entries(schema[key])
				.map(([name, child]) => [
					name,
					convertSchemaNode(child, root)
				]));
		}
	}

	for(const key of schemaListKeywords) {
		const children = schema[key];

		if(Array.isArray(children)) {
			schema[key] = children.map(child => convertSchemaNode(child, root));
		}
	}

	for(const key of schemaKeywords) {
		if(schema[key] !== undefined) {
			schema[key] = convertSchemaNode(schema[key], root);
		}
	}

	if(Array.isArray(schema.items)) {
		schema.items = schema.items.map(child => convertSchemaNode(child, root));
	}
	else if(schema.items !== undefined) {
		schema.items = convertSchemaNode(schema.items, root);
	}

	if(isObject(schema.dependencies)) {
		schema.dependencies = Object.fromEntries(Object.entries(schema.dependencies)
			.map(([name, dependency]) => [
				name,
			Array.isArray(dependency) ? dependency : convertSchemaNode(dependency, root)
			]));
	}

	if(value.errorMessage === undefined && typeof schema.errorMessage !== "string" && isObject(value.properties) && Array.isArray(value.required)) {
		const message = isObject(schema.errorMessage) ? {
			...schema.errorMessage
		} : {};
		const required = isObject(message.required) ? {
			...message.required
		} : {};

		if(typeof message.required !== "string") {
			for(const name of value.required) {
				const property = typeof name === "string" ? resolveLocalSchema(value.properties[name], root) : undefined;
				const propertyMessage = property && property.errorMessage === undefined
					? validationMessage(property.validationMessage ?? property["x-validationMessage"])
					: undefined;
				const missingMessage = typeof propertyMessage === "string" ? propertyMessage
					: isObject(propertyMessage) ? propertyMessage.required ?? propertyMessage._ : undefined;

				if(typeof name === "string" && typeof missingMessage === "string") {
					required[name] = missingMessage;
				}
			}

			if(Object.keys(required).length) {
				message.required = required;
				schema.errorMessage = message;
			}
		}
	}

	return schema;
}

/** Normalize schema declarations without changing data literals or requiring version metadata. */
export function normalizeJsonSchema(value: unknown): unknown {
	if(!isObject(value)) {
		return value;
	}

	const converted = convertSchemaNode(value, value) as JsonObject;
	const metadata = isObject(converted.metadata) ? converted.metadata : {};
	const legacyUi = isObject(value["x-ui"]) ? value["x-ui"] : {};

	if(metadata.form === undefined && hasLegacyPresentation(converted, converted, true)) {
		metadata.form = presentationUi(converted, converted, legacyUi.jsonForms, true);
	}

	if(legacyUi.jsonForms !== undefined) {
		const remainingUi = {
			...legacyUi
		};

		delete remainingUi.jsonForms;

		if(Object.keys(remainingUi).length > 0) {
			converted["x-ui"] = remainingUi;
		}
		else {
			delete converted["x-ui"];
		}
	}

	if(Object.keys(metadata).length > 0) {
		converted.metadata = metadata;
	}

	return converted;
}

function convertMember(owner: unknown, name: string): void {
	if(!isObject(owner)) {
		return;
	}

	const pascalName = name.charAt(0)
		.toUpperCase() + name.slice(1);

	for(const key of [
		name,
		pascalName
	]) {
		if(owner[key] !== undefined) {
			owner[key] = normalizeJsonSchema(owner[key]);
		}
	}
}

function visitCollection(value: unknown, visit: (item: unknown) => void): void {
	if(Array.isArray(value)) {
		value.forEach(visit);
	}
	else if(isObject(value)) {
		Object.values(value)
			.forEach(visit);
	}
}

function convertType(value: unknown): void {
	convertMember(value, "schema");
}

function convertAgency(value: unknown): void {
	visitCollection(readMember(value, "types"), convertType);
}

function convertForm(value: unknown): void {
	convertMember(value, "schema");
	convertMember(readMember(readMember(value, "onboarding"), "requests"), "form");
	convertAgency(readMember(value, "agency"));
	visitCollection(readMember(value, "proxies"), convertType);
}

function responseKind(url: string): ResponseKind | undefined {
	const path = url.split(/[?#]/, 1)[0].replace(/^https?:\/\/[^/]+/i, "")
		.replace(/\/$/, "");

	if(/(?:^|\/)api\/forms(?:\/\d+)?$/.test(path)) {
		return "form";
	}

	if(/(?:^|\/)api\/forms\/\d+\/proxies(?:\/\d+)?$/.test(path)) {
		return "proxy";
	}

	if(/(?:^|\/)api\/agencies(?:\/\d+)?$/.test(path)) {
		return "agency";
	}

	if(/(?:^|\/)api\/agencies\/\d+\/types(?:\/[^/]+)?$/.test(path)) {
		return "type";
	}

	return undefined;
}

/** Inspect only configuration DTO locations on known read endpoints. */
export function normalizeSchemaResponse(url: string, payload: unknown): void {
	const kind = responseKind(url);
	const convert = kind === "form" ? convertForm
		: kind === "agency" ? convertAgency
			: kind === "proxy" || kind === "type" ? convertType
				: undefined;

	if(!convert) {
		return;
	}

	if(Array.isArray(payload)) {
		payload.forEach(convert);
	}
	else {
		convert(payload);
	}
}

interface SchemaResponse {
	config?: {
		method?: string;
		url?: string;
	};
	data?: {
		Data?: unknown;
		Success?: boolean;
	};
	status?: number;
}

function applicable(response: SchemaResponse): boolean {
	return response.config?.method?.toUpperCase() === "GET"
		&& response.data?.Success !== false
		&& (response.status === undefined || response.status < 400)
		&& responseKind(response.config.url ?? "") !== undefined;
}

function modify(response: SchemaResponse): SchemaResponse {
	normalizeSchemaResponse(response.config?.url ?? "", response.data?.Data);

	return response;
}

export const jsonSchemaNormalization: [
	(response: SchemaResponse) => boolean,
	(response: SchemaResponse) => SchemaResponse
] = [applicable, modify];
